import React, { useState } from "react"
import { Card, Button, Table, Tag, Flex, Typography, Collapse } from "antd"
import { UpOutlined } from "@ant-design/icons"
import ExternalLink from "ui/Icon/DSIcon/ExternalLink"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import BookingEmptyImage from "ui/Icon/DSIcon/BookingEmptyImage"
import { generateBookingLink } from "../../../modal/DeactivateDelete/utils"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const { Title, Text } = Typography

/**
 * BookingsSection component displays upcoming bookings table and mobile cards
 * @param {Array} bookings - Array of booking date groups
 * @param {number} totalCount - Total count of bookings
 * @param {Function} onAddBooking - Handler for add booking button
 * @param {Function} t - Translation function
 */
const BookingsSection = ({ bookings = [], totalCount, onAddBooking, t }) => {
  const [collapsedDateGroups, setCollapsedDateGroups] = useState({})
  const [showAllBookings, setShowAllBookings] = useState(false)
  const { hasPermission } = usePermissions()

  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)

  // Toggle collapse state for a date group in mobile view
  const toggleDateGroup = (date) => {
    setCollapsedDateGroups((prev) => ({
      ...prev,
      [date]: !prev[date]
    }))
  }

  // Handle "See More" button click
  const handleSeeMore = () => {
    setShowAllBookings(true)
  }

  // Table columns for bookings
  const bookingColumns = [
    {
      title: t("equipment.details.columns.experience_name"),
      dataIndex: "experienceName",
      key: "experienceName",
      render: (text, record) => (
        <a href={generateBookingLink(record)} target="_blank" rel="noopener noreferrer" className={styles.experienceLink}>
          {text} <ExternalLink />
        </a>
      )
    },
    {
      title: t("equipment.details.columns.time"),
      dataIndex: "time",
      key: "time"
    },
    {
      title: t("equipment.details.columns.id"),
      dataIndex: "bookingId",
      key: "bookingId"
    },
    {
      title: t("equipment.details.columns.client_name"),
      dataIndex: "clientName",
      key: "clientName"
    },
    {
      title: t("equipment.details.columns.crew_members"),
      dataIndex: "crewMembers",
      key: "crewMembers"
    },
    {
      title: t("equipment.details.columns.equipment"),
      dataIndex: "equipment",
      key: "equipment"
    },
    {
      title: t("equipment.details.columns.payment_status"),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status) => <Tag color={status === "Paid" ? "green" : "orange"}>{status}</Tag>
    }
  ]

  return (
    <Card className={styles.bookingsCard}>
      <Flex justify="space-between" align="center" className={styles.sectionHeader}>
        <Title level={4}>
          {t("equipment.details.upcoming_bookings")} <span className={styles.totalCount}>({totalCount})</span>
        </Title>
        {canCreateBooking && (
          <Button type="primary" onClick={onAddBooking}>
            {t("equipment.details.add_new_booking")}
          </Button>
        )}
      </Flex>
      <div className={styles.scrollContainer}>
        <WithEmptyStateWrapper
          name="booking"
          hasItems={bookings.length > 0}
          fallbackTitle={t("equipment.details.no_upcoming_bookings")}
          fallbackButtonText={canCreateBooking ? t("equipment.details.add_new_booking") : null}
          fallbackButtonClickHandler={canCreateBooking ? onAddBooking : null}
          fallbackImageSrc={<BookingEmptyImage />}
        >
          <Collapse
            defaultActiveKey={[bookings[0]?.date]}
            expandIconPosition="end"
            className={styles.bookingsCollapse}
            items={bookings.map((dateGroup) => ({
              key: dateGroup.date,
              label: <Text strong>{dateGroup.date}</Text>,
              children: (
                <Table
                  dataSource={dateGroup.bookings}
                  columns={bookingColumns}
                  pagination={false}
                  rowKey="id"
                  className={styles.bookingsTable}
                  scroll={{ x: 800 }}
                />
              )
            }))}
          />
        </WithEmptyStateWrapper>
      </div>
      <div className={styles.mobileBookingsContainer}>
        {bookings.length > 0 ? (
          <>
            {(showAllBookings ? bookings : bookings.slice(0, 3)).map((dateGroup) => (
              <div key={dateGroup.date} className={styles.mobileDateGroup}>
                <div className={styles.mobileDateHeader} onClick={() => toggleDateGroup(dateGroup.date)}>
                  <Title level={4}>{dateGroup.date}</Title>
                  <UpOutlined className={`${styles.chevronIcon} ${collapsedDateGroups[dateGroup.date] ? styles.collapsed : ""}`} />
                </div>
                {!collapsedDateGroups[dateGroup.date] &&
                  dateGroup.bookings.map((booking) => (
                    <div key={booking.id} className={styles.mobileBookingCard}>
                      <div className={styles.bookingTitle}>
                        <a href={generateBookingLink(booking)} target="_blank" rel="noopener noreferrer">
                          {booking.experienceName}
                          <ExternalLink />
                        </a>
                      </div>
                      <div className={styles.bookingTags}>
                        <span className={`${styles.paymentTag} ${booking.paymentStatus === "Paid" ? styles.paid : ""}`}>
                          {booking.paymentStatus}
                        </span>
                        <span className={styles.idTag}>ID: {booking.bookingId}</span>
                      </div>
                      <div className={styles.bookingTime}>
                        <p>{booking.time}</p>
                        <div className={styles.dotSeparator} />
                        <p>{booking.clientName}</p>
                      </div>
                      <div className={styles.bookingDetails}>
                        <div className={styles.bookingDetailItem}>
                          <Text>Crew Member(s):</Text>
                          <span>{booking.crewMembers}</span>
                        </div>
                        <div className={styles.dotSeparator} />
                        <div className={styles.bookingDetailItem}>
                          <Text>Equipment:</Text>
                          <span>{booking.equipment}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ))}
            {!showAllBookings && bookings.length > 3 && (
              <div className={styles.seeMoreLink}>
                <Button type="link" onClick={handleSeeMore}>
                  See More
                </Button>
              </div>
            )}
          </>
        ) : (
          <Flex vertical align="center" justify="center" className={styles.emptyState}>
            <Text type="secondary">{t("equipment.details.no_upcoming_bookings")}</Text>
          </Flex>
        )}
      </div>
    </Card>
  )
}

BookingsSection.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      bookings: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
        })
      ).isRequired
    })
  ),
  totalCount: PropTypes.number,
  onAddBooking: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}

export default BookingsSection
